import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Card,
  Chip,
  Button,
} from '@mui/material';
import {
  VisibilityOutlined,
  Schedule,
  InsertDriveFile,
} from '@mui/icons-material';
import type { Issue } from '../../store/api/githubApi';
import {
  getIssueImpactLevel,
  parseEffortMinutes,
  formatEffortMinutes,
} from './simulatorUtils';

interface RecommendedFixesProps {
  issues: Issue[];
  selectedIssueIds: Set<number>;
  onToggleIssue: (issueId: number) => void;
  onSelectTop: (count: number) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onViewIssue: (issue: Issue) => void;
}

export const RecommendedFixes: React.FC<RecommendedFixesProps> = ({
  issues,
  selectedIssueIds,
  onToggleIssue,
  onSelectTop,
  onSelectAll,
  onClearSelection,
  onViewIssue,
}) => {
  const selectedCount = selectedIssueIds.size;

  const getSeverityStyle = (sev?: string) => {
    switch ((sev ?? '').toLowerCase()) {
      case 'critical':
        return {
          label: 'Critical',
          color: '#dc2626',
          bg: '#fef2f2',
          border: '#fecaca',
        };
      case 'high':
        return {
          label: 'High',
          color: '#ea580c',
          bg: '#fff7ed',
          border: '#fed7aa',
        };
      case 'medium':
        return {
          label: 'Medium',
          color: '#d97706',
          bg: '#fffbeb',
          border: '#fde68a',
        };
      case 'low':
        return {
          label: 'Low',
          color: '#2563eb',
          bg: '#eff6ff',
          border: '#bfdbfe',
        };
      default:
        return {
          label: 'Info',
          color: '#64748b',
          bg: '#f8fafc',
          border: '#e2e8f0',
        };
    }
  };

  const getImpactBadge = (impact: 'HIGH' | 'MEDIUM' | 'LOW') => {
    switch (impact) {
      case 'HIGH':
        return {
          color: '#b91c1c',
          bg: '#fef2f2',
          border: '#fca5a5',
        };
      case 'MEDIUM':
        return {
          color: '#c2410c',
          bg: '#fff7ed',
          border: '#fdba74',
        };
      case 'LOW':
        return {
          color: '#1d4ed8',
          bg: '#eff6ff',
          border: '#93c5fd',
        };
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header & Selection Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          pb: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: '#0f172a',
              fontSize: '1.1rem',
              letterSpacing: '-0.01em',
            }}
          >
            Recommended Fixes
          </Typography>
          <Chip
            label={`${selectedCount} fix${selectedCount !== 1 ? 'es' : ''} selected`}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              bgcolor: selectedCount > 0 ? '#eff6ff' : '#f1f5f9',
              color: selectedCount > 0 ? '#2563eb' : '#64748b',
              border: '1px solid',
              borderColor: selectedCount > 0 ? '#bfdbfe' : '#e2e8f0',
            }}
          />
        </Box>

        {/* Quick select buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button
            size="small"
            onClick={() => onSelectTop(3)}
            sx={{
              fontSize: '0.78rem',
              py: 0.4,
              px: 1,
              color: '#475569',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#f1f5f9', color: '#0f172a' },
            }}
          >
            Top 3
          </Button>
          <Button
            size="small"
            onClick={onSelectAll}
            sx={{
              fontSize: '0.78rem',
              py: 0.4,
              px: 1,
              color: '#475569',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#f1f5f9', color: '#0f172a' },
            }}
          >
            Select All
          </Button>
          {selectedCount > 0 && (
            <Button
              size="small"
              onClick={onClearSelection}
              sx={{
                fontSize: '0.78rem',
                py: 0.4,
                px: 1,
                color: '#94a3b8',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { color: '#dc2626' },
              }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {/* Issues List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {issues.slice(0, 15).map((issue) => {
          const isSelected = selectedIssueIds.has(issue.id);
          const sevStyle = getSeverityStyle(issue.severity);
          const impact = getIssueImpactLevel(issue);
          const impactStyle = getImpactBadge(impact);
          const effortMinutes = parseEffortMinutes(issue);
          const effortFormatted = formatEffortMinutes(effortMinutes);

          return (
            <Card
              key={issue.id}
              elevation={0}
              onClick={() => onToggleIssue(issue.id)}
              sx={{
                cursor: 'pointer',
                p: { xs: 2, sm: 2.25 },
                borderRadius: '14px',
                border: '1.5px solid',
                borderColor: isSelected ? '#2563eb' : '#e2e8f0',
                bgcolor: isSelected ? '#f8faff' : '#ffffff',
                boxShadow: isSelected
                  ? '0 4px 14px -2px rgba(37, 99, 235, 0.12)'
                  : '0 1px 3px rgba(0,0,0,0.03)',
                transition: 'all 0.18s ease',
                '&:hover': {
                  borderColor: isSelected ? '#1d4ed8' : '#cbd5e1',
                  boxShadow: '0 4px 12px -2px rgba(15, 23, 42, 0.07)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                {/* Checkbox */}
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleIssue(issue.id);
                  }}
                  sx={{
                    p: 0.25,
                    mt: 0.2,
                    color: '#cbd5e1',
                    '&.Mui-checked': {
                      color: '#2563eb',
                    },
                  }}
                />

                {/* Finding Details */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Title */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: '#0f172a',
                      fontSize: '0.95rem',
                      lineHeight: 1.4,
                      mb: 0.5,
                    }}
                  >
                    {issue.title}
                  </Typography>

                  {/* Category & Severity + File path */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: '#475569',
                        textTransform: 'capitalize',
                      }}
                    >
                      {issue.category || 'General'}
                    </Typography>

                    <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                      ·
                    </Typography>

                    <Chip
                      label={sevStyle.label}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        bgcolor: sevStyle.bg,
                        color: sevStyle.color,
                        border: `1px solid ${sevStyle.border}`,
                      }}
                    />

                    {issue.file_path && (
                      <>
                        <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                          ·
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            maxWidth: { xs: '100%', sm: 320 },
                          }}
                        >
                          <InsertDriveFile sx={{ fontSize: 13, color: '#94a3b8', flexShrink: 0 }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {issue.file_path.split('/').pop()}
                            {issue.line_start ? `:${issue.line_start}` : ''}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>

                  {/* Short issue detail / explanation if available */}
                  {issue.ai_explanation && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '0.82rem',
                        lineHeight: 1.45,
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {issue.ai_explanation}
                    </Typography>
                  )}

                  {/* Metrics and Action Footer */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      pt: 0.75,
                      borderTop: '1px solid #f1f5f9',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                      {/* Impact indicator */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: '#64748b', fontSize: '0.76rem' }}
                        >
                          Impact:
                        </Typography>
                        <Chip
                          label={impact}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 800,
                            letterSpacing: '0.04em',
                            bgcolor: impactStyle.bg,
                            color: impactStyle.color,
                            border: `1px solid ${impactStyle.border}`,
                          }}
                        />
                      </Box>

                      {/* Estimated effort */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Schedule sx={{ fontSize: 14, color: '#94a3b8' }} />
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: '#64748b', fontSize: '0.76rem' }}
                        >
                          Estimated effort:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontWeight: 700,
                            color: '#0f172a',
                            fontSize: '0.78rem',
                          }}
                        >
                          {effortFormatted}
                        </Typography>
                      </Box>
                    </Box>

                    {/* View Issue Action */}
                    <Button
                      size="small"
                      variant="text"
                      endIcon={<VisibilityOutlined sx={{ fontSize: 15 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewIssue(issue);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: '#2563eb',
                        py: 0.3,
                        px: 1,
                        borderRadius: '6px',
                        '&:hover': {
                          bgcolor: 'rgba(37, 99, 235, 0.08)',
                        },
                      }}
                    >
                      View Issue
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default RecommendedFixes;
